import type { FunnelPlannerContent } from "@/lib/content/entries/funnel-planner";
import type { AppLocale, RegionalSettings } from "@/lib/funnel-planner/schema";
import { RTL_LOCALES } from "@/lib/i18n/constants";

type Values = Record<string, string | number>;
export type FunnelTextDirection = "ltr" | "rtl";

const LOCALE_TAGS: Record<string, string> = {
  "pt-br": "pt-BR",
  "zh-cn": "zh-CN",
};

const CHANNEL_GROUP_KEYS: Record<string, string> = {
  "Search Organic": "searchOrganic",
  "Search Paid": "searchPaid",
  "Social Organic": "socialOrganic",
  "Social Paid": "socialPaid",
  "Video": "video",
  "Email & Messaging": "emailAndMessaging",
  "Content & PR": "contentAndPR",
  "Partnerships": "partnerships",
  "Display & Programmatic": "displayAndProgrammatic",
  "Retail Media": "retailMedia",
  "AI Search / AEO": "aiSearchAEO",
  "Print & Broadcast": "printAndBroadcast",
  "Retargeting": "retargeting",
  "Other": "other",
};

export function toIntlLocale(locale: AppLocale | string): string {
  return LOCALE_TAGS[locale] ?? locale;
}

export function directionForFunnelLocale(locale: AppLocale | string): FunnelTextDirection {
  return (RTL_LOCALES as readonly string[]).includes(locale) ? "rtl" : "ltr";
}

export function textAlignForFunnelLocale(locale: AppLocale | string): "left" | "right" {
  return directionForFunnelLocale(locale) === "rtl" ? "right" : "left";
}

export function funnelText(
  content: FunnelPlannerContent,
  key: string,
  fallback = "",
  values?: Values,
): string {
  const raw = (content as unknown as Record<string, unknown>)[key];
  const source = typeof raw === "string" && raw.length > 0 ? raw : fallback;
  if (!values) return source;
  return source.replace(/\{(\w+)\}/g, (match, name: string) => {
    const value = values[name];
    return value == null ? match : String(value);
  });
}

export function formatFunnelCurrency(
  value: number,
  locale: AppLocale | string,
  settings?: Pick<RegionalSettings, "currency"> | null,
): string {
  return new Intl.NumberFormat(toIntlLocale(locale), {
    style: "currency",
    currency: settings?.currency ?? "USD",
    maximumFractionDigits: 0,
  }).format(value);
}

export function formatFunnelNumber(value: number, locale: AppLocale | string): string {
  return new Intl.NumberFormat(toIntlLocale(locale), {
    maximumFractionDigits: 0,
  }).format(value);
}

export function formatFunnelPercent(value: number, locale: AppLocale | string): string {
  return new Intl.NumberFormat(toIntlLocale(locale), {
    maximumFractionDigits: 1,
  }).format(value) + "%";
}

export function formatFunnelDate(value: Date, locale: AppLocale | string): string {
  return new Intl.DateTimeFormat(toIntlLocale(locale), {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(value);
}

export function formatFunnelShortDate(value: Date, locale: AppLocale | string): string {
  return new Intl.DateTimeFormat(toIntlLocale(locale)).format(value);
}

export function keySegment(value: string): string {
  return value
    .replace(/&/g, "and")
    .replace(/\+/g, "plus")
    .replace(/[^a-zA-Z0-9]+(.)?/g, (_match, chr: string | undefined) => chr ? chr.toUpperCase() : "")
    .replace(/^[A-Z]/, (chr) => chr.toLowerCase());
}

export function stageTypeLabel(
  content: FunnelPlannerContent,
  stageType: string | undefined,
  fallback = "Custom",
): string {
  const id = stageType ?? "custom";
  return funnelText(content, `stageTypes.${id}.label`, fallback);
}

export function channelLabel(
  content: FunnelPlannerContent,
  channelId: string,
  fallback: string,
): string {
  return funnelText(content, `channels.${channelId}.label`, fallback);
}

export function channelGroupLabel(
  content: FunnelPlannerContent,
  group: string,
): string {
  return funnelText(content, `channelGroups.${CHANNEL_GROUP_KEYS[group] ?? keySegment(group)}`, group);
}

export function acquisitionModeLabel(
  content: FunnelPlannerContent,
  mode: string,
  fallback: string,
): string {
  return funnelText(content, `acquisitionModes.${mode}`, fallback);
}

export function productTypeLabel(
  content: FunnelPlannerContent,
  type: string,
  fallback: string,
): string {
  return funnelText(content, `productTypes.${type}`, fallback);
}

export function priceTypeLabel(
  content: FunnelPlannerContent,
  type: string,
  fallback: string,
): string {
  return funnelText(content, `priceTypes.${type}`, fallback);
}

export function guaranteeLabel(
  content: FunnelPlannerContent,
  type: string,
  fallback: string,
): string {
  return funnelText(content, `guarantees.${type}`, fallback);
}
